import React, { useEffect, useState } from 'react';

import Button from '@mui/material/Button';
import Icon from '@mui/material/Icon';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import { openModal } from 'components/Modal/modalReducer';
import useRouter from 'hooks/useRouter';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from 'store/actions';

import AGGridTable from '../../../views/AGGrid/AGGrid';
import { formatNumberToCurrency, numberWithCommas } from '../../../helpers/utils';
import { toast } from 'react-toastify';

const useStyles = makeStyles((theme) => ({
  ctaButton: {
    borderRadius: 60,
    height: '100%',
    width: theme.spacing(17),
    color: theme.palette.primary.main,
    background: theme.palette.common.white,
    fontWeight: 'bold',
    boxShadow: 'none',
    border: '1px solid #5381EF',
    '&:hover': {
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.common.white
    },
    '&:hover .MuiIcon-root': {
      color: 'white !important'
    },
    '&:hover .MuiIconButton-root:hover': {
      backgroundColor: 'transparent !important'
    }
  }
}));

const formatCurrencyValue = (value) => {
  const numericValue = Number(value);

  if (!numericValue || !Number.isFinite(numericValue)) {
    return '-';
  }

  return formatNumberToCurrency(numericValue, 'EUR', '-', 2);
};

const formatPercentageValue = (value) => {
  const numericValue = Number(value);

  if (!numericValue || !Number.isFinite(numericValue)) {
    return '-';
  }

  const fixedValue = numericValue.toFixed(2);
  const formattedValue = fixedValue.endsWith('.00')
    ? numberWithCommas(Math.round(numericValue))
    : numberWithCommas(fixedValue);

  return `${formattedValue}%`;
};

const currencyColumn = (column) => ({
  ...column,
  valueFormatter: ({ value }) => formatCurrencyValue(value),
  useValueFormatterForExport: true,
  editable: true,
  cellDataType: 'number'
});

const percentageColumn = (column) => ({
  ...column,
  valueFormatter: ({ value }) => formatPercentageValue(value),
  useValueFormatterForExport: true,
  editable: true,
  cellDataType: 'number'
});

const Valuations = () => {
  const router = useRouter();
  const query = router.query;
  const dispatch = useDispatch();
  const classes = useStyles();
  const { valuations, loaded, error } = useSelector((state) => state.valuations);
  const { data: asset } = useSelector((state) => state.assets.assetDetails);
  const assetId = asset.id || query.id;

  useEffect(() => {
    if (!query.id || (query.id === asset.id && loaded)) {
      return;
    }
    dispatch(actions.getValuations(query.id));
  }, []);

  const handleNew = () => {
    dispatch(
      openModal({
        modalType: 'ValuationModal',
        modalProps: {}
      })
    );
  };

  const numericFields = [
    'marketvalue',
    'liquidationvalue',
    'dcfmethodvalue',
    'comparativemethodvalue',
    'insurancemethodvalue',
    'currentrent',
    'erv',
    'discountrate',
    'exityield'
  ];

  const getValuationForm = (rowData) => ({
    valuation: rowData.valuation || '',
    date: rowData.date ? new Date(rowData.date) : new Date(),
    valuer: rowData.valuer || '',
    marketvalue: rowData.marketvalue || 0,
    liquidationvalue: rowData.liquidationvalue || 0,
    summary: rowData.summary || '',
    assetid: rowData.assetid || assetId,
    id: rowData.id,
    dcfmethodvalue: rowData.dcfmethodvalue || 0,
    comparativemethodvalue: rowData.comparativemethodvalue || 0,
    insurancemethodvalue: rowData.insurancemethodvalue || 0,
    currentrent: rowData.currentrent || 0,
    erv: rowData.erv || 0,
    discountrate: rowData.discountrate || 0,
    exityield: rowData.exityield || 0
  });

  const updateValuation = (value, api) => {
    const field = value.colDef.field;

    if (value.newValue === value.oldValue) {
      return;
    }

    if (field === 'valuation' && !value.newValue) {
      value.data[field] = value.oldValue;
      api.refreshCells({ rowNodes: [value.node], columns: [field] });
      toast.error('Valuation is required');
      return;
    }

    if (numericFields.includes(field)) {
      value.data[field] = value.newValue === '' || value.newValue === null ? 0 : Number(value.newValue);
    }

    dispatch(actions.createValuation(getValuationForm(value.data), assetId))
      .then(() => {
        dispatch(actions.getValuations(assetId));
      })
      .catch((err) => {
        value.data[field] = value.oldValue;
        api.refreshCells({ rowNodes: [value.node], columns: [field] });
        toast.error(err);
      });
  };

  const [colDefs] = useState([
    {
      headerName: '',
      field: 'rowSelection',
      checkboxSelection: true,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      maxWidth: 60
    },
    {
      headerName: 'Valuation',
      field: 'valuation',
      editable: true
    },
    {
      headerName: 'Date',
      field: 'date',
      cellDataType: 'date',
      filter: 'agDateColumnFilter',
      editable: true
    },
    currencyColumn({
      headerName: 'Fair Market Value',
      field: 'marketvalue'
    }),
    currencyColumn({
      headerName: 'Liquidation Value',
      field: 'liquidationvalue'
    }),
    {
      headerName: 'Valuer',
      field: 'valuer',
      editable: true
    },
    currencyColumn({
      headerName: 'Dcf Method Value',
      field: 'dcfmethodvalue'
    }),
    currencyColumn({
      headerName: 'Comparative Method Value',
      field: 'comparativemethodvalue'
    }),
    currencyColumn({
      headerName: 'Insurance Method Value',
      field: 'insurancemethodvalue'
    }),
    currencyColumn({
      headerName: 'Current Rent (EUR/month)',
      field: 'currentrent'
    }),
    currencyColumn({
      headerName: 'ERV (EUR/sqm/month)',
      field: 'erv'
    }),
    percentageColumn({
      headerName: 'Discount Rate',
      field: 'discountrate'
    }),
    percentageColumn({
      headerName: 'Exit Yield',
      field: 'exityield'
    }),
  ]);

  if (error) {
    return (
      <Typography
        variant="h6"
        color="error"
        style={{
          marginTop: 50,
          marginLeft: 50
        }}
      >
        {error}
      </Typography>
    );
  }

  return (
    <AGGridTable
      buttons={[
        <Button
          className={classes.ctaButton}
          style={{ marginRight: '10px' }}
          startIcon={<Icon style={{ color: '#5381EF' }}>add</Icon>}
          onClick={handleNew}
        >
          VALUATION
        </Button>
      ]}
      data={valuations}
      colDefs={colDefs}
      updateHandler={updateValuation}
      deleteHandler={(selectedRows, api) => {
        api.applyTransaction({ remove: selectedRows });
        for (let i = 0; i < selectedRows.length; i++) {
          dispatch(actions.deleteValuation(selectedRows[i].id))
            .then(() => {})
            .catch((err) => toast.error(err));
        }
      }}
      classes={classes}
    />
  );
};

export default Valuations;
